'use client';

/**
 * The one muted file row for the whole app (LF-4642) — white monochrome
 * type tile, charcoal progress, quiet success + small green tick, semantic-red
 * error. Presentational + primitive props, so BOTH client `File` uploads
 * (via `FileDropZoneItem`) and server-owned records without a `File` handle
 * (e.g. Intake's persisted documents) render through the same row.
 *
 * The optional `footer` slot renders bespoke per-file controls INSIDE the card,
 * below the main row (e.g. the matter Documents dialog's "certified copy"
 * toggle) — so options live in the row card, not floating beside it.
 */

import type { ReactNode } from 'react';
import { Check, X, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type FileRowStatus = 'idle' | 'uploading' | 'success' | 'error';

export interface FileRowProps {
  /** Short uppercase file-type label for the monochrome tile, e.g. 'PDF'. */
  label: string;
  /**
   * Optional image thumbnail (object URL / data URI). When set, it replaces the
   * monochrome type-tile with a preview — for surfaces where seeing the image
   * matters (letterhead, signatures). The caller owns the URL's lifecycle.
   */
  previewUrl?: string;
  name: string;
  status?: FileRowStatus;
  /** Preformatted size, e.g. '2.4 MB'. Shown in idle / success. */
  sizeLabel?: string;
  /** Upload progress 0–100. Omit in `uploading` to show "Uploading…" instead. */
  progress?: number;
  error?: string;
  /** Extra sub-line content (e.g. a domain source badge). */
  meta?: ReactNode;
  /**
   * Optional controls rendered INSIDE the card, below the main row, separated
   * by a hairline divider (e.g. a per-file "certified copy" toggle).
   */
  footer?: ReactNode;
  onRemove?: () => void;
  onRetry?: () => void;
  /** aria-label for the remove control. */
  removeLabel?: string;
}

export function FileRow({
  label,
  previewUrl,
  name,
  status = 'idle',
  sizeLabel,
  progress,
  error,
  meta,
  footer,
  onRemove,
  onRetry,
  removeLabel
}: FileRowProps) {
  const isError = status === 'error';

  return (
    <div
      className={cn(
        'bg-background w-full rounded-lg border p-2.5 transition-colors',
        isError
          ? 'border-destructive/30'
          : 'border-border hover:border-muted-foreground/30'
      )}
      data-status={status}
    >
      <div className="flex items-center gap-3">
        {/* Image preview when provided (letterhead / signature); otherwise the
            monochrome file-type tile — the label carries the type, no colour. */}
        {previewUrl ? (
          // eslint-disable-next-line @next/next/no-img-element -- object URL / data URI preview; next/image impractical for these dynamic sources
          <img
            src={previewUrl}
            alt=""
            aria-hidden="true"
            className="border-border bg-background size-9 shrink-0 rounded-md border object-cover"
          />
        ) : (
          <span
            className="border-border bg-background text-foreground text-9px grid size-9 shrink-0 place-items-center rounded-md border px-0.5 font-mono leading-none font-bold tracking-tight"
            aria-hidden="true"
          >
            {label}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <p className="text-foreground truncate text-sm font-medium">{name}</p>
          <FileRowStatusLine
            status={status}
            sizeLabel={sizeLabel}
            progress={progress}
            error={error}
            meta={meta}
          />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {isError && !!onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="border-destructive/40 text-destructive hover:bg-destructive/10 text-12px inline-flex items-center gap-1 rounded-md border px-2 py-1 font-medium transition-colors"
            >
              <RotateCcw className="size-3" aria-hidden="true" />
              Retry
            </button>
          )}
          {isError && (
            <span className="bg-destructive/10 text-destructive grid size-5.5 place-items-center rounded-full">
              <AlertTriangle className="size-3" aria-hidden="true" />
            </span>
          )}
          {!!onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={
                removeLabel ??
                (status === 'uploading' ? 'Cancel upload' : 'Remove file')
              }
              className="text-muted-foreground hover:bg-muted hover:text-foreground grid size-7 place-items-center rounded-md transition-colors"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {!!footer && (
        <div className="border-border/70 mt-2.5 border-t pt-2.5">{footer}</div>
      )}
    </div>
  );
}

function FileRowStatusLine({
  status,
  sizeLabel,
  progress,
  error,
  meta
}: Pick<FileRowProps, 'status' | 'sizeLabel' | 'progress' | 'error' | 'meta'>) {
  if (status === 'uploading') {
    // Callers with no progress concept get the plain indeterminate label.
    if (typeof progress !== 'number') {
      return <p className="text-muted-foreground mt-1 text-xs">Uploading…</p>;
    }
    // Determinate track — the bar is always shown. The S3 PUT can sit queued at
    // 0% for seconds before bytes move, so in the right-hand slot show a spinner
    // in place of "0%" until progress starts, then swap to the percentage.
    const started = progress > 0;
    return (
      <div className="mt-1 flex items-center gap-2">
        <span className="bg-muted h-1.5 w-full max-w-[190px] overflow-hidden rounded-full">
          <span
            className="bg-foreground block h-full rounded-full transition-[width] duration-200"
            style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
          />
        </span>
        {started ? (
          <span className="text-muted-foreground text-12px font-mono tabular-nums">
            {Math.round(progress)}%
          </span>
        ) : (
          <Loader2
            className="text-muted-foreground size-3 shrink-0 animate-spin"
            aria-label="Uploading"
          />
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <p className="text-destructive mt-0.5 text-xs font-medium">
        {error ?? 'Upload failed'}
      </p>
    );
  }

  if (status === 'success') {
    return (
      <div className="mt-0.5 flex items-center gap-2">
        {meta}
        <span className="text-muted-foreground text-xs">Uploaded</span>
        {!!sizeLabel && (
          <span className="text-muted-foreground/80 text-12px font-mono tabular-nums">
            {sizeLabel}
          </span>
        )}
        <span
          className="text-success-subtle-foreground inline-flex"
          aria-label="Uploaded"
        >
          <Check className="size-3" aria-hidden="true" />
        </span>
      </div>
    );
  }

  // idle
  return (
    <div className="mt-0.5 flex items-center gap-2">
      {meta}
      {!!sizeLabel && (
        <span className="text-muted-foreground text-12px font-mono tabular-nums">
          {sizeLabel}
        </span>
      )}
    </div>
  );
}
